import React, { useState, useRef, useEffect } from 'react';
import { Bell, Search, User, ChevronDown, LogOut, Settings, UserCircle, TrendingUp, FileText, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const SEARCH_ITEMS = [
    { label: 'Investment', path: '/investment', icon: TrendingUp, description: 'Investment contract analysis' },
    { label: 'Bonds', path: '/bonds', icon: FileText, description: 'Bond contract analysis' },
    { label: 'Profile Settings', path: '/settings?tab=profile', icon: UserCircle, description: 'Manage your profile' },
    { label: 'Account Settings', path: '/settings?tab=account', icon: Settings, description: 'Account and security settings' },
    { label: 'Help & Support', path: '/help', icon: HelpCircle, description: 'Get help and contact support' },
];

export const Topbar = ({ title }) => {
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [selectedSearchIndex, setSelectedSearchIndex] = useState(0);
    const dropdownRef = useRef(null);
    const searchRef = useRef(null);
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    // Filter search results
    const searchResults = searchQuery.trim()
        ? SEARCH_ITEMS.filter(item =>
            item.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.description.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : [];

    const showSearchResults = isSearchFocused && searchResults.length > 0;

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsProfileOpen(false);
            }
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setIsSearchFocused(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle keyboard navigation in search
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!showSearchResults) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedSearchIndex(prev => 
                    prev < searchResults.length - 1 ? prev + 1 : prev
                );
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedSearchIndex(prev => prev > 0 ? prev - 1 : 0);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                if (searchResults[selectedSearchIndex]) {
                    handleSearchSelect(searchResults[selectedSearchIndex]);
                }
            } else if (e.key === 'Escape') {
                setIsSearchFocused(false);
                setSearchQuery('');
            }
        };

        if (isSearchFocused) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isSearchFocused, searchResults, selectedSearchIndex]);

    // Reset selected index when search query changes
    useEffect(() => {
        setSelectedSearchIndex(0);
    }, [searchQuery]);

    const handleSearchSelect = (item) => {
        navigate(item.path);
        setSearchQuery('');
        setIsSearchFocused(false);
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8 lg:px-8 px-20 sticky top-0 z-10">
            <h1 className="text-xl font-semibold text-slate-800">{title}</h1>

            <div className="flex items-center gap-6">
                {/* Search */}
                <div className="relative hidden md:block" ref={searchRef}>
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none z-10" />
                    <input
                        type="text"
                        placeholder="Search menus..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setIsSearchFocused(true)}
                        className="pl-10 pr-4 py-2 rounded-full border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-64 text-sm transition-all"
                    />
                    
                    {/* Search Results Dropdown */}
                    {showSearchResults && (
                        <div className="absolute top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-slate-200 py-2 animate-fade-in z-50 max-h-96 overflow-y-auto">
                            <div className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                Search Results ({searchResults.length})
                            </div>
                            {searchResults.map((item, index) => {
                                const ItemIcon = item.icon;
                                return (
                                    <button
                                        key={item.path}
                                        onClick={() => handleSearchSelect(item)}
                                        className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${
                                            index === selectedSearchIndex
                                                ? 'bg-primary-50 border-l-2 border-primary-500'
                                                : 'hover:bg-slate-50'
                                        }`}
                                    >
                                        <div className={`mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                            index === selectedSearchIndex
                                                ? 'bg-primary-100'
                                                : 'bg-slate-100'
                                        }`}>
                                            <ItemIcon className={`h-4 w-4 ${
                                                index === selectedSearchIndex
                                                    ? 'text-primary-600'
                                                    : 'text-slate-600'
                                            }`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm font-medium ${
                                                index === selectedSearchIndex
                                                    ? 'text-primary-900'
                                                    : 'text-slate-900'
                                            }`}>
                                                {item.label}
                                            </p>
                                            <p className="text-xs text-slate-500 truncate">
                                                {item.description}
                                            </p>
                                        </div>
                                    </button>
                                );
                            })}
                            <div className="px-4 py-2 mt-2 border-t border-slate-200 text-xs text-slate-500">
                                Use ↑↓ to navigate, Enter to select, Esc to close
                            </div>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-4">
                    <button className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors relative">
                        <Bell className="h-5 w-5" />
                        <span className="absolute top-2 right-2 h-2 w-2 bg-danger-500 rounded-full border-2 border-white"></span>
                    </button>

                    {/* Profile Dropdown */}
                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={() => setIsProfileOpen(!isProfileOpen)}
                            className="flex items-center gap-2 hover:bg-slate-50 rounded-lg px-2 py-1 transition-colors"
                        >
                            <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center border border-primary-200">
                                <User className="h-5 w-5 text-primary-700" />
                            </div>
                            <div className="hidden lg:block text-left">
                                <p className="text-sm font-medium text-slate-900">{user?.name || 'User'}</p>
                                <p className="text-xs text-slate-500">{user?.email || 'user@example.com'}</p>
                            </div>
                            <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {/* Dropdown Menu */}
                        {isProfileOpen && (
                            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-slate-200 py-2 animate-fade-in z-50">
                                {/* User Info */}
                                <div className="px-4 py-3 border-b border-slate-200">
                                    <p className="text-sm font-semibold text-slate-900">{user?.name || 'User'}</p>
                                    <p className="text-xs text-slate-500 mt-1">{user?.email || 'user@example.com'}</p>
                                </div>

                                {/* Menu Items */}
                                <div className="py-2">
                                    <a
                                        href="/settings?tab=profile"
                                        className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            window.location.href = '/settings?tab=profile';
                                            setIsProfileOpen(false);
                                        }}
                                    >
                                        <UserCircle className="h-4 w-4" />
                                        Profile Settings
                                    </a>
                                    <a
                                        href="/settings?tab=account"
                                        className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            window.location.href = '/settings?tab=account';
                                            setIsProfileOpen(false);
                                        }}
                                    >
                                        <Settings className="h-4 w-4" />
                                        Account Settings
                                    </a>
                                </div>

                                {/* Logout */}
                                <div className="border-t border-slate-200 pt-2">
                                    <button
                                        onClick={handleLogout}
                                        className="flex items-center gap-3 px-4 py-2 text-sm text-danger-600 hover:bg-danger-50 transition-colors w-full"
                                    >
                                        <LogOut className="h-4 w-4" />
                                        Logout
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};
